import { useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useParams } from "react-router";
import { api } from "../../../convex/_generated/api";
import { isBackendError } from "@repo/types";
import { identifyPostHogUser } from "@repo/utils";
import { useCandidateAuthStore } from "./candidateAuthStore";
import { LastNameModal } from "./last-name-modal";

export function AuthGuard() {
  const { id } = useParams<{ id: string }>();
  const { token, setEvaluationId, setToken, setFirstName, setEmail, setParcoursId } =
    useCandidateAuthStore();

  const urlToken = new URLSearchParams(window.location.search).get("token") ?? undefined;

  // État local du token en cours de validation
  const [currentLastName, setCurrentLastName] = useState<string | undefined>(
    urlToken || token || undefined,
  );
  const [error, setError] = useState<string | undefined>();
  const [blockedUntil, setBlockedUntil] = useState<number | undefined>();

  // Sync après réhydratation Zustand async (le store s'initialise avec "" avant de lire le cookie)
  useEffect(() => {
    if (!currentLastName && (urlToken || token)) {
      setCurrentLastName(urlToken || token);
    }
  }, [token, urlToken, currentLastName]);

  // Type d'évaluation, sans authentification — sert à l'affichage de la modale
  const typeResult = useQuery(
    api.evaluations.obtenirTypeEvaluation,
    id ? { evaluationId: id } : "skip",
  );

  // Type d'évaluation issu de la query non authentifiée
  const evaluationType =
    typeResult && "typeEval" in typeResult ? typeResult.typeEval : undefined;

  // Retrouve parcoursId depuis evaluationId (aucune authentification requise)
  const parcoursLookup = useQuery(
    api.parcoursEvaluations.obtenirParcoursIdDepuisEvaluation,
    id ? { evaluationId: id as any } : "skip",
  );
  const parcoursId = parcoursLookup?.parcoursId;

  // Récupère les métadonnées du parcours pour savoir s'il est anonyme
  const parcoursMetadata = useQuery(
    api.parcoursEvaluations.obtenirMetadonneesParcours,
    parcoursId ? { parcoursId: parcoursId as any } : "skip",
  );
  const estAnonyme = parcoursMetadata?.anonyme === true;

  // Utilise la query générique d'évaluation de evaluations.ts
  const result = useQuery(
    api.evaluations.testConnexion,
    id && currentLastName && parcoursId
      ? { evaluationId: id, parcoursId, token: currentLastName }
      : "skip",
  );

  // Met à jour le token stocké quand la validation réussit
  useEffect(() => {
    if (
      result &&
      "statut" in result &&
      result.statut === "ok" &&
      id &&
      currentLastName &&
      parcoursId
    ) {
      setToken(currentLastName);
      setEvaluationId(id);
      setParcoursId(parcoursId);
      setFirstName(result.prenomCandidat ?? "");
      const email = result.emailCandidat ?? "";
      setEmail(email);
      if (email) identifyPostHogUser(email);
      setError(undefined);
      setBlockedUntil(undefined);
    }
  }, [
    result,
    id,
    currentLastName,
    parcoursId,
    setToken,
    setEvaluationId,
    setParcoursId,
    setFirstName,
    setEmail,
  ]);

  // Soumission de la modale
  const handleLastNameSubmit = (token: string) => {
    setError(undefined);
    setBlockedUntil(undefined);
    setCurrentLastName(token);
  };

  // Aucun identifiant dans l'URL
  if (!id) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Lien invalide</h1>
          <p className="mt-2 text-muted-foreground">
            Le lien d'evaluation est invalide ou incomplet.
          </p>
        </div>
      </div>
    );
  }

  // Pas encore de token : on affiche la modale
  if (!currentLastName) {
    return (
      <LastNameModal
        isOpen={true}
        onSubmit={handleLastNameSubmit}
        error={error}
        blockedUntil={blockedUntil}
        evaluationType={evaluationType}
        isAnonymous={estAnonyme}
      />
    );
  }

  // État de chargement
  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Erreur remontée par la query
  if (isBackendError(result)) {
    if (result.erreur === "Évaluation introuvable") {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-destructive">
              Évaluation introuvable
            </h1>
            <p className="mt-2 text-muted-foreground">
              L'évaluation demandée n'existe pas ou a été supprimée.
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <LastNameModal
          isOpen={true}
          onSubmit={handleLastNameSubmit}
          error={result.erreur}
          blockedUntil={result.bloqueJusquA}
          evaluationType={evaluationType}
          isAnonymous={estAnonyme}
        />
      );
    }
  }

  return <Outlet />;
}
